/**
 * Keypair Wallet Service
 *
 * Provides Ethereum wallet functionality for vanilla keypairs.
 * The secp256k1 private key used for encryption IS compatible with Ethereum,
 * so we can derive an ETH address and perform transactions directly.
 */

import { Wallet, JsonRpcProvider, formatEther, parseEther } from 'ethers'

// RPC URLs for supported chains
const RPC_URLS: Record<number, string> = {
  1: 'https://eth.drpc.org',
  8453: 'https://mainnet.base.org',  // Base (for escrow)
  100: 'https://rpc.gnosischain.com', // Gnosis (for xBZZ operations)
  11155111: 'https://rpc.sepolia.org', // Sepolia testnet
  84532: 'https://sepolia.base.org',  // Base Sepolia (for escrow testing)
}

// Default to Gnosis for production (xBZZ operations)
const DEFAULT_CHAIN_ID = 100

/**
 * Get the ETH address derived from a secp256k1 private key
 * @param privateKeyHex - Hex string of the private key (with or without 0x prefix)
 */
export function getAddressFromPrivateKey(privateKeyHex: string): string {
  const key = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
  const wallet = new Wallet(key)
  return wallet.address
}

/**
 * Get wallet balance for a keypair
 * @param privateKeyHex - Hex string of the private key
 * @param chainId - Chain ID to query (default: Base Sepolia)
 */
export async function getKeypairBalance(
  privateKeyHex: string,
  chainId: number = DEFAULT_CHAIN_ID
): Promise<{ balance: string; formatted: string }> {
  const rpcUrl = RPC_URLS[chainId]
  if (!rpcUrl) {
    throw new Error(`Unsupported chain: ${chainId}`)
  }

  const key = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = new Wallet(key, provider)

  const balance = await provider.getBalance(wallet.address)
  return {
    balance: balance.toString(),
    formatted: formatEther(balance),
  }
}

/**
 * Send ETH from a keypair wallet
 * @param privateKeyHex - Hex string of the private key
 * @param toAddress - Recipient address
 * @param amountEth - Amount in ETH (e.g., "0.1")
 * @param chainId - Chain ID (default: Base Sepolia)
 */
export async function sendFromKeypair(
  privateKeyHex: string,
  toAddress: string,
  amountEth: string,
  chainId: number = DEFAULT_CHAIN_ID
): Promise<{ txHash: string; explorerUrl: string }> {
  const rpcUrl = RPC_URLS[chainId]
  if (!rpcUrl) {
    throw new Error(`Unsupported chain: ${chainId}`)
  }

  const key = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = new Wallet(key, provider)

  const tx = await wallet.sendTransaction({
    to: toAddress,
    value: parseEther(amountEth),
  })

  const receipt = await tx.wait()
  if (!receipt) {
    throw new Error('Transaction failed')
  }

  // Get explorer URL
  const explorerUrls: Record<number, string> = {
    1: 'https://etherscan.io',
    8453: 'https://basescan.org',
    100: 'https://gnosisscan.io',
    11155111: 'https://sepolia.etherscan.io',
    84532: 'https://sepolia.basescan.org',
  }

  const explorerBase = explorerUrls[chainId] || 'https://etherscan.io'

  return {
    txHash: tx.hash,
    explorerUrl: `${explorerBase}/tx/${tx.hash}`,
  }
}

/**
 * Get a wallet signer for contract interactions
 * @param privateKeyHex - Hex string of the private key
 * @param chainId - Chain ID (default: Base Sepolia)
 */
export function getKeypairSigner(
  privateKeyHex: string,
  chainId: number = DEFAULT_CHAIN_ID
): Wallet {
  const rpcUrl = RPC_URLS[chainId]
  if (!rpcUrl) {
    throw new Error(`Unsupported chain: ${chainId}`)
  }

  const key = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
  const provider = new JsonRpcProvider(rpcUrl)
  return new Wallet(key, provider)
}

/**
 * Chain metadata for UI
 * Sepolia first - DataEscrow contract deployed there
 */
export const SUPPORTED_CHAINS = [
  { id: 11155111, name: 'Sepolia', symbol: 'ETH', isTestnet: true },
  { id: 84532, name: 'Base Sepolia', symbol: 'ETH', isTestnet: true },
  { id: 8453, name: 'Base', symbol: 'ETH', isTestnet: false },
  { id: 100, name: 'Gnosis', symbol: 'xDAI', isTestnet: false },
  { id: 1, name: 'Ethereum', symbol: 'ETH', isTestnet: false },
]

export const DEFAULT_ESCROW_CHAIN_ID = 8453 // Base - DataEscrow deployed here
